import React, { useState } from 'react';
import { FileCode, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { notificaErrore, notificaConferma, notificaAvviso, messaggioDa } from '../../utils/notifiche';
import '../../styles/StatoSdi.css';

/**
 * StatoSdi — lo stato della fattura elettronica di un documento, e i due
 * gesti di questo blocco (blocco 6, migration 258):
 *   - «Scarica XML»: il file da caricare sul portale Fatture e
 *     Corrispettivi (provider «manuale»);
 *   - «Rigenera l'XML»: dopo aver corretto quello che il motivo dice.
 * Chi decide se il documento va allo SdI, e se l'XML e' valido, e' il
 * server: qui si mostra e basta.
 */
export const STATI_SDI = {
    non_dovuta:       { testo: 'non va allo SdI', classe: 'nulla' },
    da_correggere:    { testo: 'XML da correggere', classe: 'errore' },
    pronta:           { testo: 'XML pronto', classe: 'pronta' },
    inviata:          { testo: 'inviata allo SdI', classe: 'attesa' },
    consegnata:       { testo: 'consegnata', classe: 'ok' },
    mancata_consegna: { testo: 'mancata consegna', classe: 'attesa' },
    scartata:         { testo: 'scartata dallo SdI', classe: 'errore' },
};
const SCARICABILE = ['pronta', 'inviata', 'consegnata', 'mancata_consegna', 'scartata'];
const RIGENERABILE = ['da_correggere', 'pronta', 'scartata'];

const StatoSdi = ({ fattura, onCambiata, compatto = false }) => {
    const [inCorso, setInCorso] = useState(false);
    const st = STATI_SDI[fattura.fe_stato];
    if (!st) return compatto ? <span className="sdi-nessuno">—</span> : null;

    const scarica = async () => {
        try {
            const r = await api.get(`/fatturazione/${fattura.id}/xml`, { responseType: 'blob' });
            const nome = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `fattura_${fattura.id}.xml`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = nome;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            /* La risposta e' un blob anche quando e' un errore: il motivo
               sta dentro, in JSON. */
            let msg = 'Non riesco a scaricare la fattura elettronica';
            try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (x) { /* resta il ripiego */ }
            notificaErrore(msg);
        }
    };

    const rigenera = async () => {
        setInCorso(true);
        try {
            const r = await api.post(`/fatturazione/${fattura.id}/xml`);
            if (r.data?.data?.stato === 'pronta') notificaConferma(r.data.message);
            else notificaAvviso(r.data?.message || 'La fattura elettronica è ancora da correggere.');
            if (onCambiata) onCambiata();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a rigenerare la fattura elettronica'));
        } finally {
            setInCorso(false);
        }
    };

    return (
        <span className={`sdi ${compatto ? 'sdi--compatto' : ''}`}>
            <span className={`sdi-stato sdi-${st.classe}`}
                  title={fattura.fe_stato === 'da_correggere' ? (fattura.fe_motivo || '') : undefined}>
                {st.testo}
            </span>
            {SCARICABILE.includes(fattura.fe_stato) && (
                <button type="button" className="sdi-btn" onClick={scarica} title="Scarica l'XML da caricare sul portale">
                    <FileCode size={14} aria-hidden="true" />{!compatto && ' Scarica XML'}
                </button>
            )}
            {RIGENERABILE.includes(fattura.fe_stato) && fattura.fe_stato !== 'pronta' && (
                <button type="button" className="sdi-btn" onClick={rigenera} disabled={inCorso}
                        title="Rifà l'XML dopo aver corretto quello che il motivo dice">
                    <RefreshCw size={14} aria-hidden="true" />{!compatto && ' Rigenera l\'XML'}
                </button>
            )}
            {!compatto && fattura.fe_stato === 'da_correggere' && fattura.fe_motivo && (
                <span className="sdi-motivo">{fattura.fe_motivo}</span>
            )}
        </span>
    );
};

export default StatoSdi;
